import React, { useState, useEffect } from 'react';
import CreateNewGallery from '../../components/CreateNewGallery';
import CustomDialog from '../../components/CustomDialog';
import { axiosInstance } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar.js';
import AdminSectionLayout from '../../components/AdminSectionLayout';

export default function Galleries() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalGalleriesCount, setTotalGalleriesCount] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [galleryToDelete, setGalleryToDelete] = useState(null);
  const itemsPerPage = 10;

  const handleSetupNewClick = () => {
    setEditingGallery(null);
    setShowCreateGallery(true);
  };

  const handleCancelClick = () => {
    setShowCreateGallery(false);
    setEditingGallery(null);
  };

  const handleGalleryCreated = () => {
    setShowCreateGallery(false);
    setEditingGallery(null);
    fetchGalleries(); // Refresh the galleries list
  };

  const handleEditClick = (gallery) => {
    setEditingGallery(gallery);
    setShowCreateGallery(true);
  };

  const handleDeleteClick = (gallery) => {
    setGalleryToDelete(gallery);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (galleryToDelete) {
      try {
        await axiosInstance.delete(`/gallery/${galleryToDelete._id}`);
        setShowDeleteDialog(false);
        setGalleryToDelete(null);
        fetchGalleries(); // Refresh the galleries list
      } catch (error) {
        console.error('Error deleting gallery:', error);
        alert('Failed to delete gallery. Please try again.');
        setShowDeleteDialog(false);
        setGalleryToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setGalleryToDelete(null);
  };

  const fetchGalleries = async () => {
    try {
      const response = await axiosInstance.get(`/gallery?page=${page}&limit=${itemsPerPage}&search=${searchQuery}&searchId=${searchQuery}`);
      console.log('Response data:', response.data);
      setGalleries(response.data.galleries);
      setTotalGalleriesCount(response.data.totalGalleriesCount);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    }
  };

  useEffect(() => {
    fetchGalleries();
  }, [page, searchQuery]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // If the input is exactly 4 digits, trigger the search immediately
    if (/^\d{4}$/.test(value)) {
      fetchGalleries();
    }
  };

  return (
    <>
    <Navbar />
    <AdminSectionLayout
      title="Galleries"
      description="Manage portal galleries with this interface."
      actions={
        <>
          <input
            type="text"
            placeholder="Search by gallery name"
            className="w-full sm:w-80 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <button
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            onClick={handleSetupNewClick}
          >
            Setup New
          </button>
        </>
      }
    >
      <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 text-sm overflow-hidden">
  <div className="max-h-[600px] overflow-y-auto overflow-x-auto"> {/* Adjust the max-height as needed */}
    <table className="w-full min-w-[780px] table-fixed">
      <thead className="sticky top-0 bg-white z-10 border-b border-gray-100">
        <tr>
          <th className="pl-4 sm:pl-6 py-2 text-left">#</th>
          <th className="pl-4 sm:pl-6 py-2 text-left">Gallery Name</th>
          <th className="pl-4 sm:pl-6 py-2 text-left">Gallery ID</th>
          <th className="pl-4 sm:pl-6 py-2 text-left">Date Created</th>
          <th className="pl-4 sm:pl-6 py-2 text-left">Actions</th>
        </tr>
      </thead>
      <tbody className="text-base headFont">
        {galleries.map((gallery, index) => (
          <tr key={gallery._id}>
            <td className="px-4 sm:px-10 py-3 border-t">
              {(page - 1) * itemsPerPage + index + 1}
            </td>
            <td className="px-4 sm:px-10 py-3 border-t border-l">
              <Link to={`/gallery-management/${gallery._id}`} className="text-blue-500 hover:underline">
                {gallery.displayName}
              </Link>
            </td>
            <td className='px-4 sm:px-10 py-3 border-t border-l'>
              {gallery.galleryId}
            </td>
            <td className="px-4 sm:px-10 py-2 border-t border-l">
              {new Date(gallery.createdAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </td>
            <td className="px-4 sm:px-10 py-2 border-t border-l">
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => handleEditClick(gallery)}
                  className="px-3 py-1 text-sm text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteClick(gallery)}
                  className="px-3 py-1 text-sm text-white bg-red-500 rounded hover:bg-red-600 focus:outline-none"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
        <p className="text-sm text-gray-500">
          Showing {(page - 1) * itemsPerPage + 1} -{' '}
          {Math.min(page * itemsPerPage, totalGalleriesCount)} of {totalGalleriesCount} galleries
        </p>
        <div className="flex">
          <button
            onClick={() => setPage((prevPage) => Math.max(prevPage - 1, 1))}
            disabled={page === 1}
            className={`px-3 py-1.5 text-sm mr-2 ${
              page === 1
                ? 'text-gray-400 bg-gray-200'
                : 'text-white bg-blue-500 '
            } rounded`}
          >
            Previous
          </button>
          <button
            onClick={() => setPage((prevPage) => prevPage + 1)}
            disabled={galleries.length < itemsPerPage}
            className={`px-3 py-1.5 text-sm ${
              galleries.length < itemsPerPage
                ? 'text-gray-400 bg-gray-200'
                : 'text-white bg-blue-500 '
            } rounded`}
          >
            Next
          </button>
        </div>
      </div>

      <CreateNewGallery 
        isOpen={showCreateGallery} 
        onCancel={handleCancelClick}
        onSuccess={handleGalleryCreated}
        editingGallery={editingGallery}
      />

      <CustomDialog
        isOpen={showDeleteDialog}
        title="Delete Gallery"
        message={`Are you sure you want to delete "${galleryToDelete?.displayName}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </AdminSectionLayout>
    </>
  );
}
